/**
 * PeekContainer Component
 * Phase 8: Peek Surfaces - Main Container
 * 
 * A slide-in panel component that provides a floating surface for quick access
 * to tools like AI Chat and Quick Note. Features:
 * - Slide-in animation from right side
 * - Draggable resize handle for width adjustment
 * - Header with title and close button
 * - Escape key to close
 * - Only one peek panel can be open at a time
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PeekContainer.css';

export interface PeekContainerProps {
  /** Unique identifier for the peek panel */
  id: string;
  /** Title displayed in the header */
  title: string;
  /** Content to render inside the peek panel */
  children: React.ReactNode;
  /** Whether the peek panel is currently open */
  isOpen: boolean;
  /** Callback when close button is clicked or Escape is pressed */
  onClose: () => void;
  /** Initial width of the peek panel (default: 320px) */
  width?: number;
  /** Minimum width for resize (default: 250px) */
  minWidth?: number;
  /** Maximum width for resize (default: 600px) */
  maxWidth?: number;
  /** Custom className for additional styling */
  className?: string;
  /** Whether to show the resize handle (default: true) */
  showResizeHandle?: boolean;
  /** Callback when width is changed */
  onWidthChange?: (width: number) => void;
  /** V5.1+cockpit: Info Chip (origin: where this peek came from) */
  originLabel?: string;
  /** V5.1+cockpit: Support-Act chips (peek actions like Delete/Duplicate/Share) */
  supportActs?: React.ReactNode;
  /** V5.1+cockpit: Breadcrumb path shown in footer */
  breadcrumb?: React.ReactNode;
  /**
   * F 371 — the control row in the peek's top bar.
   *
   * The container does not know what it is showing, so it cannot decide
   * which controls belong there. It renders a slot; the caller fills it.
   * See PeekTopBar for the DatabaseView's answer.
   */
  topBar?: React.ReactNode;
}

/**
 * PeekContainer Component
 * 
 * Renders a slide-in panel from the right side of the screen that can contain
 * various surfaces like AI Chat or Quick Note.
 */
export const PeekContainer: React.FC<PeekContainerProps> = ({
  id,
  title,
  children,
  isOpen,
  onClose,
  width = 320,
  minWidth = 250,
  maxWidth = 600,
  className = '',
  showResizeHandle = true,
  onWidthChange,
  originLabel,
  supportActs,
  breadcrumb,
  topBar,
}) => {
  const [currentWidth, setCurrentWidth] = useState(width);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  /**
   * Handle escape key to close peek panel
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onClose]);

  /**
   * Handle mouse down on resize handle to start resizing
   */
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  /**
   * Handle mouse move during resize
   */
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = window.innerWidth - e.clientX;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setCurrentWidth(newWidth);
        onWidthChange?.(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minWidth, maxWidth, onWidthChange]);

  return (
    <>
      {/* Backdrop - click to close */}
      {isOpen && (
        <div
          className="peek-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Peek Container */}
      <div
        ref={containerRef}
        className={`peek-container ${isOpen ? 'open' : 'closed'} ${className}`}
        style={{
          width: `${currentWidth}px`,
        }}
        data-testid={`peek-container-${id}`}
      >
        {/* Resize Handle */}
        {showResizeHandle && (
          <div
            ref={resizeHandleRef}
            className={`peek-resize-handle ${isResizing ? 'active' : ''}`}
            onMouseDown={handleResizeMouseDown}
            title="Drag to resize"
            aria-label="Resize peek panel"
            role="slider"
            aria-valuenow={currentWidth}
            aria-valuemin={minWidth}
            aria-valuemax={maxWidth}
          />
        )}

        {/* Header */}
        <div className="peek-header">
          <div className="peek-header-content">
            {originLabel && <span className="peek-info-chip" title="Origin">{originLabel}</span>}
            <h2 className="peek-title">{title}</h2>
          </div>
          {/* F 371 — the Notion-style control row, supplied by the caller. */}
          {topBar && <div className="peek-topbar-slot">{topBar}</div>}
          <button
            className="peek-close-button"
            onClick={onClose}
            title="Close peek panel (Esc)"
            aria-label={`Close ${title}`}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* V5.1+cockpit: Support-Act chip row (Delete/Duplicate/Share) */}
        {supportActs && <div className="peek-support-acts">{supportActs}</div>}

        {/* Content Area */}
        <div className="peek-content">
          {children}
        </div>

        {/* V5.1+cockpit: Breadcrumb footer */}
        {breadcrumb && <div className="peek-breadcrumb">{breadcrumb}</div>}
      </div>
    </>
  );
};

export default PeekContainer;
